import { CTAButton, Link } from '@buoyhealth/common.buoy-button';
import { ErrorText } from '@buoyhealth/common.buoy-input';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { NEWSLETTERS } from '../../constants';
import { EmailCollectionContext } from '../../contexts';
import NewEmailView from './NewEmailView';
import SubmittedEmailView from './SubmittedEmailView';
import EmailSuccessMessage from '../SuccessMessage';

import type { SubmitHandler } from 'react-hook-form';
import type { EmailCollectionFormData } from '../../types';

type Props = {
  className?: string;
  descriptionText?: string;
  onSubmit?: (data: EmailCollectionFormData) => void;
  submitText?: string;
  submitSuccessMessage?: string;
  variant?: 'inline' | 'modal';
};

const DEFAULT_NEWSLETTERS = [NEWSLETTERS.studiesShow.value];

function EmailCollectionForm({
  className = '',
  descriptionText = 'Get a copy of your visit sent to your email',
  onSubmit,
  submitText = 'Submit',
  submitSuccessMessage,
  variant = 'inline',
}: Props) {
  const [editing, setEditing] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const { email: contextEmail, setEmail: setContextEmail } =
    useContext(EmailCollectionContext) || {};

  const [isPreviouslySubmitted] = useState(!!contextEmail);

  const {
    control,
    formState: { isSubmitting, isSubmitSuccessful },
    handleSubmit: formSubmit,
    setValue,
    watch,
    reset,
  } = useForm<EmailCollectionFormData>({
    defaultValues: {
      email: contextEmail || '',
      newsletterOptIn: true,
      newsletters: DEFAULT_NEWSLETTERS,
      termsAgreed: !!contextEmail,
    },
  });

  const handleEdit = useCallback(() => {
    reset(undefined, { keepValues: true });
    setEditing(true);
  }, []);

  const handleSubmit: SubmitHandler<EmailCollectionFormData> = useCallback(
    async (data) => {
      try {
        setContextEmail?.(data.email);
        setEditing(false);
        onSubmit?.(data);
      } catch {
        setSubmitError(true);
      }
    },
    [onSubmit, setContextEmail],
  );

  const newsletters = watch('newsletters');
  const newsletterOptIn = watch('newsletterOptIn');

  useEffect(() => {
    if (!newsletters.length) {
      setValue('newsletterOptIn', false);
    }
    if (newsletterOptIn && !newsletters.length) {
      setValue('newsletters', DEFAULT_NEWSLETTERS);
    }
  }, [newsletterOptIn, newsletters, setValue]);

  const isSubmittedEmailView = isPreviouslySubmitted && !editing;

  return (
    <div
      className={`bp-flex bp-flex-col bp-items-start bp-justify-center bp-gap-3 bp-rounded-lg bp-bg-blue-0 bp-p-4 ${className}`}
    >
      {isSubmittedEmailView ? (
        <SubmittedEmailView
          descriptionText={descriptionText}
          email={watch('email')}
          onEdit={handleEdit}
        />
      ) : (
        <NewEmailView descriptionText={descriptionText} formControl={control} />
      )}

      {submitError && (
        <ErrorText
          show={submitError}
          message={'An error occurred while submitting. Please try again.'}
        />
      )}

      {isSubmitSuccessful && variant === 'inline' && submitSuccessMessage && (
        <EmailSuccessMessage className="bp-w-full" submitSuccessMessage={submitSuccessMessage} />
      )}

      {isSubmittedEmailView && (
        <CTAButton
          size="small"
          disabled={(isSubmitting || isSubmitSuccessful) && !submitError}
          onClick={formSubmit(handleSubmit)}
          fullWidth
          sx={{
            padding: '0.5rem 1.5rem !important',
            '>span': {
              width: '100%',
            },
          }}
        >
          <span className="bp-font-primary-small bp-flex bp-w-full bp-justify-between">
            {isSubmitSuccessful && !submitError ? 'Success!' : 'Send a copy'}
            <span>→</span>
          </span>
        </CTAButton>
      )}

      {!isSubmittedEmailView && (
        <>
          <CTAButton
            className="bp-mt-2"
            disabled={(isSubmitting || isSubmitSuccessful) && !submitError}
            onClick={formSubmit(handleSubmit)}
            fullWidth
          >
            {isSubmitSuccessful && !submitError ? 'Success!' : submitText}
          </CTAButton>
          <p className="bp-font-primary-smallest bp-mt-1">
            By submitting, I agree to Buoy&apos;s{' '}
            <Link href="/terms" target="_blank" className="bp-text-xxs">
              Terms of Use
            </Link>{' '}
            and{' '}
            <Link href="/privacy" target="_blank" className="bp-text-xxs">
              Privacy Policy
            </Link>
            {
              '. I also agree to receive occasional marketing emails from Buoy and I understand that I may opt out of Buoy subscriptions at any time.'
            }
          </p>
        </>
      )}
    </div>
  );
}

export default EmailCollectionForm;
